type TargetOrgType = 'client' | 'supplier';
type TargetOrgTypeOrSubEntity = TargetOrgType | 'federated';

const TARGET_ORG_TYPE_KEY: InjectionKey<Ref<TargetOrgTypeOrSubEntity>> = Symbol('targetOrgType');

/**
 * Provides the type of org being viewed on the other side of a connection.
 * Call in top-level profile pages (SupplierConnectedProfile, ClientProfile),
 * DiscussionView (derived from clientId/supplierId props), and SubEntityProfile
 * (provides 'federated').
 *
 * Can be called at any level to override the parent-provided value for a subtree.
 */
export function provideTargetOrgType(type: MaybeRefOrGetter<TargetOrgTypeOrSubEntity>) {
  provide(TARGET_ORG_TYPE_KEY, toRef(type));
}

/**
 * Like {@link useTargetOrgType} but accepts 'federated' — for components that render
 * in both client/supplier and federated contexts (e.g. org-info).
 */
export function useTargetOrgTypeOrSubEntity(
  fallback?: MaybeRefOrGetter<TargetOrgTypeOrSubEntity>,
): Ref<TargetOrgTypeOrSubEntity> {
  const targetOrgType = inject(TARGET_ORG_TYPE_KEY, null);
  if (targetOrgType) return targetOrgType;
  if (fallback !== undefined) return toRef(fallback);
  throw new Error('useTargetOrgType() called outside a provider');
}

/**
 * Injects the type of org being viewed, as provided by an ancestor via provideTargetOrgType().
 * Mirrors the behaviour of `inject`: if a fallback is supplied it is used when no provider
 * is present — useful for composables that can be called from both profile and non-profile
 * contexts. Throws if called outside a provider and no fallback is given.
 *
 * Throws at access time if the provided value is 'federated' — use
 * `useTargetOrgTypeOrSubEntity` from federated-aware components.
 */
export function useTargetOrgType(fallback?: MaybeRefOrGetter<TargetOrgType>): Ref<TargetOrgType> {
  const wide = useTargetOrgTypeOrSubEntity(fallback);
  return computed(() => {
    if (wide.value === 'federated') {
      throw new Error('useTargetOrgType() got federated — use useTargetOrgTypeOrSubEntity instead');
    }
    return wide.value;
  });
}
